package com.siriusxm.pia.views.unifiedaggregator.management

import androidx.compose.runtime.Composable
import com.siriusxm.pia.components.MessageType
import com.siriusxm.pia.components.confirmationDialogWithContent
import com.siriusxm.pia.components.messageBox
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

/**
 * A confirmation dialog displayed when a user removes one or more types from a producer or consumer.
 * Lists the types being removed and requires the user to type "CONFIRM" to proceed.
 * This prevents accidental removal of types which could disrupt content flow.
 *
 * @param entityType The type of entity being edited ("producer" or "consumer")
 * @param removedTypeNames The display names of the types being removed
 * @param onConfirm Callback invoked with true if confirmed, false if cancelled
 */
@Composable
fun TypeRemovalConfirmationDialog(
    entityType: String,
    removedTypeNames: List<String>,
    onConfirm: suspend (Boolean) -> Unit
) {
    confirmationDialogWithContent(
        title = "Confirm Type Removal",
        action = "Remove Types",
        confirmationText = "CONFIRM",
        confirmationInstruction = "Type 'CONFIRM' to proceed with removing these types",
        cb = onConfirm
    ) { _, _ ->
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(12.px)
            }
        }) {
            messageBox(MessageType.WARNING) {
                Text("You are about to remove ${removedTypeNames.size} type${if (removedTypeNames.size > 1) "s" else ""} from this $entityType.")
            }

            Div({
                style {
                    fontSize(0.9.em)
                }
            }) {
                Text("The following types will be removed:")
                Ul {
                    removedTypeNames.forEach { typeName ->
                        Li {
                            Text(typeName)
                        }
                    }
                }
                Text("Removing types from a $entityType could disrupt content flow. Please confirm you want to proceed.")
            }
        }
    }
}

/**
 * A warning dialog displayed when a user adds a type to a producer that is already produced
 * by another producer. Lists the conflicting types and their current producers, and requires
 * the user to type "I understand" to proceed. This prevents unintentional duplicate content
 * production across services.
 *
 * @param duplicateInfo A list of pairs where each pair is (type display name, conflicting producer name)
 * @param onConfirm Callback invoked with true if confirmed, false if cancelled
 */
@Composable
fun DuplicateTypeWarningDialog(
    duplicateInfo: List<Pair<String, String>>,
    onConfirm: suspend (Boolean) -> Unit
) {
    confirmationDialogWithContent(
        title = "Duplicate Type Warning",
        action = "Continue",
        confirmationText = "I understand",
        confirmationInstruction = "Type 'I understand' to proceed",
        cb = onConfirm
    ) { _, _ ->
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(12.px)
            }
        }) {
            messageBox(MessageType.WARNING) {
                Text("One or more types you are adding are already produced by another service. Adding them could lead to duplicate content.")
            }

            Div({
                style {
                    fontSize(0.9.em)
                }
            }) {
                Text("The following types are already assigned to other producers:")
                Ul {
                    duplicateInfo.forEach { (typeName, producerName) ->
                        Li {
                            Text("$typeName — currently produced by $producerName")
                        }
                    }
                }
            }
        }
    }
}
